import { useEffect, useMemo, useState } from 'react';
import { KrybotService } from '../../services/api/krybotService';
import { fetchCompanyChatRecipients } from '../../services/fetchCompanyChatRecipients';
import { useUser } from '../../contexts/UserContext';
import { useSurfaceContext, type ChatRecipientSuggestion } from '../../contexts/SurfaceContext';
import {
  companyRowToPersonOption,
  excludeSelfPersonOptions,
  mergeRecipientOptions,
} from './chatRecipientDirectoryHelpers';

export interface ChatRecipientOption {
  value: string;
  kind: 'agent' | 'person';
  id: string;
  label: string;
  subtitle?: string;
}

function toOption(suggestion: ChatRecipientSuggestion): ChatRecipientOption {
  return {
    value: `${suggestion.kind}:${suggestion.id}`,
    kind: suggestion.kind,
    id: suggestion.id,
    label: suggestion.label,
    subtitle: suggestion.subtitle,
  };
}

export function useChatRecipientDirectory() {
  const { effectiveContext } = useSurfaceContext();
  const { selectedCompany, user } = useUser();
  const [agentOptions, setAgentOptions] = useState<ChatRecipientOption[]>([]);
  const [personOptions, setPersonOptions] = useState<ChatRecipientOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    void KrybotService.listAgents({ agent_type: 'chat', is_active: true })
      .then((agents) => {
        if (cancelled) return;
        setAgentOptions(
          agents.map((agent) => ({
            value: `agent:${agent.id}`,
            kind: 'agent',
            id: agent.id,
            label: agent.display_name?.trim() || agent.name,
            subtitle: 'Agent',
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setAgentOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const companyId = selectedCompany?.id;
    if (!companyId) {
      setPersonOptions([]);
      return () => {
        cancelled = true;
      };
    }

    void fetchCompanyChatRecipients(companyId)
      .then((rows) => {
        if (cancelled) return;
        const mapped = rows.map(companyRowToPersonOption);
        setPersonOptions(excludeSelfPersonOptions(mapped, user?.id));
      })
      .catch(() => {
        if (!cancelled) setPersonOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCompany?.id, user?.id]);

  const suggestedOptions = useMemo(
    () => (effectiveContext.suggestedRecipients ?? []).map(toOption),
    [effectiveContext.suggestedRecipients]
  );

  const options = useMemo(
    () => mergeRecipientOptions(suggestedOptions, personOptions, agentOptions),
    [agentOptions, personOptions, suggestedOptions]
  );

  const filterOptions = (query: string) => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) ||
        opt.subtitle?.toLowerCase().includes(q) ||
        opt.kind.includes(q)
    );
  };

  return { options, filterOptions };
}

export function parseRecipientValues(values: string[], options: ChatRecipientOption[]): ChatRecipientOption[] {
  const map = new Map(options.map((opt) => [opt.value, opt]));
  return values.map((value) => map.get(value)).filter((opt): opt is ChatRecipientOption => Boolean(opt));
}
